<template>
  <div>
    <Nav></Nav>
    <b-container fluid>
      <b-row>
        <b-col>
          <h1>Reporte de Cambio de Inventario</h1>
        </b-col>
      </b-row>

      <!-- Selección de Parámetros del Reporte -->
      <b-row>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Sucursal:">
            <vu-select 
              v-model="selectedSucursal" 
              :options="sucursals" 
              :reduce="option => option.value"
              label="text"
              placeholder="Seleccionar Sucursal"
              @input="loadCountGroups">
            </vu-select>
          </b-form-group>
        </b-col>
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Conteo Inicial:">
            <vu-select 
              v-model="selectedInitialCount" 
              :options="countGroups" 
              :reduce="option => option.value"
              label="text"
              placeholder="Seleccionar Conteo Inicial"
              :disabled="!selectedSucursal">
            </vu-select>
          </b-form-group>
        </b-col>
        <b-col lg="4" md="6" sm="12">
          <b-form-group label="Conteo Final:">
            <vu-select 
              v-model="selectedEndCount" 
              :options="filteredEndCountGroups" 
              :reduce="option => option.value"
              label="text"
              placeholder="Seleccionar Conteo Final"
              :disabled="!selectedInitialCount">
            </vu-select>
          </b-form-group>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <b-form-group label="Producto en seguimiento:">
            <div class="d-flex align-items-center mt-2 mx-3">
              <b-form-checkbox v-model="showOnlyTrackable">
                {{ showOnlyTrackable ? 'Sí' : 'No' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col sm="12" class="d-flex justify-content-end">
          <b-button variant="primary" @click="generateReport" :disabled="!canGenerateReport">
            Generar Reporte
          </b-button>
          <b-button variant="success" class="ml-2" @click="exportToExcel" :disabled="!reportData.length">
            Exportar a Excel
          </b-button>
        </b-col>
      </b-row>

      <!-- Validación de Turnos -->
      <b-row v-if="reportData.length" class="mb-3">
        <b-col sm="12">
          <b-card :bg-variant="shiftsValidationVariant" text-variant="white">
            <b-card-title>
              <i :class="shiftsValidationIcon"></i>
              Validación de Turnos
            </b-card-title>
            <b-card-text>
              <b-row>
                <b-col md="3" sm="6">
                  <strong>Total de Turnos:</strong> {{ totalShifts }}
                </b-col>
                <b-col md="3" sm="6">
                  <strong>Procesados:</strong> {{ shiftsProcessed }}
                </b-col>
                <b-col md="3" sm="6">
                  <strong>Sin Procesar:</strong> {{ shiftsUnprocessed }}
                </b-col>
                <b-col md="3" sm="6">
                  <strong>Estado:</strong> {{ shiftsValidationMessage }}
                </b-col>
              </b-row>
              <b-row v-if="shiftsUnprocessed > 0" class="mt-2">
                <b-col sm="12">
                  <b-button
                    size="sm"
                    variant="light"
                    @click="showShiftsDetails = !showShiftsDetails">
                    {{ showShiftsDetails ? 'Ocultar' : 'Ver' }} Turnos Sin Procesar
                  </b-button>
                </b-col>
              </b-row>
              <b-row v-if="showShiftsDetails && shiftsUnprocessed > 0" class="mt-2">
                <b-col sm="12">
                  <b-list-group>
                    <b-list-group-item
                      v-for="shift in unprocessedShifts"
                      :key="shift.id"
                      class="d-flex justify-content-between align-items-center">
                      <span>
                        <strong>Turno #{{ shift.id }}</strong> -
                        Apertura: {{ formatDate(shift.open_date) }}
                        <span v-if="shift.close_date"> | Cierre: {{ formatDate(shift.close_date) }}</span>
                      </span>
                      <b-badge variant="warning">Sin Descarga de Stock</b-badge>
                    </b-list-group-item>
                  </b-list-group>
                </b-col>
              </b-row>
            </b-card-text>
          </b-card>
        </b-col>
      </b-row>

      <!-- Resumen del Reporte -->
      <b-row v-if="reportData.length" class="mb-3">
        <b-col sm="12">
          <b-card bg-variant="light">
            <b-row>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Valor Inventario Inicial:</div>
                  <div class="summary-value">${{ formatNumber(totalInitialCost) }}</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Valor de Compras:</div>
                  <div class="summary-value">${{ formatNumber(totalPurchases) }}</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Insumo para Ventas:</div>
                  <div class="summary-value">${{ formatNumber(totalCustomerSales) }}</div>
                </div>
              </b-col>
              <b-col md="3" sm="6">
                <div class="summary-item">
                  <div class="summary-label">Valor Inventario Final:</div>
                  <div class="summary-value">${{ formatNumber(totalEndCost) }}</div>
                </div>
              </b-col>
            </b-row>

            <!-- Indicador de Diferencia Total de Dinero -->
            <b-row class="mt-3">
              <b-col sm="12">
                <div class="total-difference"
                  :class="{ 'text-danger': totalDifference > 0, 'text-success': totalDifference < 0 }">
                  <div class="total-difference-label">Diferencia Total de Valor:</div>
                  <div class="total-difference-value">${{ formatNumber(totalDifference) }}</div>
                  <div class="total-difference-percent" v-if="totalInitialCost > 0">
                    ({{ formatNumber((totalDifference / totalInitialCost) * 100) }}% del inventario inicial)
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>

      <!-- Controles de Filtro -->
      <b-row v-if="reportData.length" class="mb-3">
        <b-col md="6" sm="12">
          <b-form-group label="Buscar:">
            <b-form-input v-model="filter" placeholder="Buscar por nombre, categoría, etc."></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="3" sm="6">
          <b-form-group label="Mostrar solo productos con discrepancias:">
            <div class="d-flex align-items-center mt-2">
              <b-form-checkbox v-model="showOnlyDiscrepancies" switch>
                {{ showOnlyDiscrepancies ? 'Sí' : 'No' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>
        <b-col md="3" sm="6">
          <b-form-group label="Elementos por página:">
            <b-form-select v-model="perPage" :options="[10, 25, 50, 100]"></b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Tabla del Reporte -->
      <b-row>
        <b-col sm="12">
          <b-overlay :show="loading" rounded="sm">
            <b-table v-if="reportData.length" :items="filteredReportData" :fields="fields" :per-page="perPage"
              :current-page="currentPage" :filter="filter" striped hover responsive show-empty small stacked="md"
              :sort-by.sync="sortBy" :sort-desc.sync="sortDesc">
              <!-- Formato personalizado para columnas numéricas -->
              <template #cell(supply_price)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(initial_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(initial_cost)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(stock_movements_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(stock_movements_total)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(purchase_order_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(purchase_order_total)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(customer_out_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(customer_out_total)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(end_qty)="data">
                {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
              </template>

              <template #cell(end_cost)="data">
                ${{ formatNumber(data.value) }}
              </template>

              <template #cell(difference_qty)="data">
                <span :class="{ 'text-danger': data.value < 0, 'text-success': data.value > 0 }">
                  {{ formatNumber(data.value) }} {{ data.item.supply_unit }}
                </span>
              </template>

              <template #cell(difference_total)="data">
                <span :class="{ 'text-danger': data.value > 0 }">
                  ${{ formatNumber(data.value) }}
                </span>
              </template>
            </b-table>

            <div v-if="!reportData.length && !loading" class="text-center p-5">
              <p>No hay datos disponibles. Por favor seleccione parámetros y genere un reporte.</p>
            </div>
          </b-overlay>
        </b-col>
      </b-row>

      <!-- Paginación -->
      <b-row v-if="reportData.length">
        <b-col sm="12" class="mt-3">
          <b-pagination v-model="currentPage" :total-rows="filteredReportData.length" :per-page="perPage" align="center"
            aria-controls="inventory-table"></b-pagination>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import Nav from "../components/Nav.vue";
import axios from "axios";
import * as XLSX from 'xlsx';

export default {
  name: "InventoryChangeReport",
  components: { Nav },
  data() {
    return {
      // Parámetros de selección
      selectedSucursal: null,
      selectedInitialCount: null,
      selectedEndCount: null,

      // Fuentes de datos
      sucursals: [],
      countGroups: [],
      filteredEndCountGroups: [],
      reportData: [],
      shiftsData: [],

      // Controles de tabla
      filter: "",
      perPage: 25,
      currentPage: 1,
      sortBy: 'supply_name',
      sortDesc: false,
      showOnlyDiscrepancies: false,
      showOnlyTrackable:false,
      showShiftsDetails: false,

      // Estado de UI
      loading: false,

      // Definiciones de campos de la tabla
      fields: [
        { key: 'supply_name', label: 'Insumo', sortable: true },
        { key: 'supply_category', label: 'Categoría', sortable: true },
        { key: 'supply_unit', label: 'Unidad', sortable: true },
        { key: 'supply_price', label: 'Precio Unitario', sortable: true },
        { key: 'supply_trackable', label: 'Vendible', sortable: true },

        { key: 'initial_qty', label: 'Cant. Inicial', sortable: true },
        { key: 'initial_cost', label: 'Costo Inicial', sortable: true },
        { key: 'stock_movements_qty', label: 'Cant. Movimientos', sortable: true },
        { key: 'stock_movements_total', label: 'Costo Movimientos', sortable: true },
        { key: 'purchase_order_qty', label: 'Cant. Compras', sortable: true },
        { key: 'purchase_order_total', label: 'Costo Compras', sortable: true },
        { key: 'customer_out_qty', label: 'Cant. Ventas', sortable: true },
        { key: 'customer_out_total', label: 'Costo Ventas', sortable: true },
        { key: 'end_qty', label: 'Cant. Final', sortable: true },
        { key: 'end_cost', label: 'Costo Final', sortable: true },
        { key: 'theoretical_qty', label: 'Cant. Teorica', sortable: true },
        { key: 'theoretical_total', label: 'Costo Teorico', sortable: true },
        { key: 'difference_qty', label: 'Dif. Cantidad', sortable: true },
        { key: 'difference_total', label: 'Dif. Costo', sortable: true }
      ]
    };
  },
  computed: {
    canGenerateReport() {
      return this.selectedSucursal && this.selectedInitialCount && this.selectedEndCount;
    },
    filteredReportData() {
      if (!this.showOnlyDiscrepancies) {
        return this.reportData;
      }

      return this.reportData.filter(item => {
        return Math.abs(item.difference_qty) > 0.001 || Math.abs(item.difference_total) > 0.01;
      });
    },
    totalInitialCost() {
      return this.reportData.reduce((sum, item) => sum + item.initial_cost, 0);
    },
    totalPurchases() {
      return this.reportData.reduce((sum, item) => sum + item.purchase_order_total, 0);
    },
    totalCustomerSales() {
      return this.reportData.reduce((sum, item) => sum + item.customer_out_total, 0);
    },
    totalEndCost() {
      return this.reportData.reduce((sum, item) => sum + item.end_cost, 0);
    },
    totalDifference() {
      return this.reportData.reduce((sum, item) => sum + item.difference_total, 0);
    },
    totalShifts() {
      return this.shiftsData.length;
    },
    shiftsProcessed() {
      return this.shiftsData.filter(shift => shift.stock_picking_id !== null && shift.stock_picking_id !== undefined).length;
    },
    shiftsUnprocessed() {
      return this.shiftsData.filter(shift => !shift.stock_picking_id || shift.stock_picking_id === null).length;
    },
    unprocessedShifts() {
      return this.shiftsData.filter(shift => !shift.stock_picking_id || shift.stock_picking_id === null);
    },
    shiftsValidationVariant() {
      if (this.totalShifts === 0) return 'secondary';
      if (this.shiftsUnprocessed === 0) return 'success';
      if (this.shiftsUnprocessed > this.shiftsProcessed) return 'danger';
      return 'warning';
    },
    shiftsValidationIcon() {
      if (this.totalShifts === 0) return 'fa fa-info-circle';
      if (this.shiftsUnprocessed === 0) return 'fa fa-check-circle';
      return 'fa fa-exclamation-triangle';
    },
    shiftsValidationMessage() {
      if (this.totalShifts === 0) return 'Sin turnos en el período';
      if (this.shiftsUnprocessed === 0) return 'Todos procesados';
      return 'Turnos pendientes';
    }
  },
  watch: {
    selectedInitialCount(newVal) {
      if (newVal) {
        this.filterEndCountGroups();
        this.selectedEndCount = null;
      } else {
        this.filteredEndCountGroups = [];
      }
    }
  },
  mounted() {
    this.loadSucursals();
  },
  methods: {
    loadSucursals() {
      axios.get("/sucursals")
        .then(response => {
          this.sucursals = response.data
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
            .map(sucursal => ({
              value: sucursal.id,
              text: sucursal.nombre
            }));
        })
        .catch(error => {
          this.makeToast("Error", "Error al cargar sucursales: " + error.message, "danger");
        });
    },
    loadCountGroups() {
      if (!this.selectedSucursal) return;

      // Limpiar selecciones previas cuando cambia la sucursal
      this.selectedInitialCount = null;
      this.selectedEndCount = null;
      this.filteredEndCountGroups = [];
      this.reportData = [];

      this.loading = true;
      axios.get(`/sucursal/${this.selectedSucursal}/stock/physicalcount/groups`)
        .then(response => {
          this.countGroups = response.data.filter(gp => gp.state != "canceled").map(group => ({
            value: group.id,
            text: `${group.name} (${group.count_date.substring(0, 10)})`,
            date: new Date(group.count_date)
          }));
          this.loading = false;
        })
        .catch(error => {
          this.makeToast("Error", "Error al cargar grupos de conteo: " + error.message, "danger");
          this.loading = false;
        });
    },
    
    filterEndCountGroups() {
      if (!this.selectedInitialCount) {
        this.filteredEndCountGroups = [];
        return;
      }
      
      const initialGroup = this.countGroups.find(g => g.value === this.selectedInitialCount);
      if (!initialGroup) return;
      
      // Filter count groups that have a date after the initial count date
      this.filteredEndCountGroups = this.countGroups.filter(group => {
        return group.date > initialGroup.date;
      });
    },
    async generateReport() {
      if (!this.canGenerateReport) return;

      this.loading = true;

      try {
        // Get the count group dates
        const initialGroup = this.countGroups.find(g => g.value === this.selectedInitialCount);
        const endGroup = this.countGroups.find(g => g.value === this.selectedEndCount);

        if (!initialGroup || !endGroup) {
          this.makeToast("Error", "No se encontraron los grupos de conteo seleccionados", "danger");
          this.loading = false;
          return;
        }

        // Format dates for shifts API (YYYY-MM-DD)
        const initialDate = this.formatDateForAPI(initialGroup.date);

        // For shifts validation, use end date - 1 day
        const endDateForShifts = new Date(endGroup.date);
        endDateForShifts.setDate(endDateForShifts.getDate() - 1);
        const endDate = this.formatDateForAPI(endDateForShifts);

        // Fetch report data and shifts data in parallel
        const [reportResponse, shiftsResponse] = await Promise.all([
          axios.get(`/report/inventory/change/${this.selectedSucursal}/${this.selectedInitialCount}/${this.selectedEndCount}?trackable_supplies=${this.showOnlyTrackable}`),
          axios.get(`/shifts?sucursal_id=${this.selectedSucursal}&initial_date=${initialDate}&end_date=${endDate}`)
        ]);

        // Process report data
        if (reportResponse.data == null) {
          this.reportData = [];
          console.log("No hay datos para mostrar");
        } else {
          this.reportData = reportResponse.data;
        }

        // Process shifts data
        this.shiftsData = shiftsResponse.data || [];
        this.showShiftsDetails = false;

        this.currentPage = 1;
        this.loading = false;
      } catch (error) {
        this.makeToast("Error", "Error al generar reporte: " + error.message, "danger");
        this.loading = false;
      }
    },
    formatDateForAPI(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    exportToExcel() {
      if (!this.reportData.length) return;

      // Obtener el nombre de la sucursal
      const sucursalName = this.sucursals.find(s => s.value === this.selectedSucursal)?.text || 'Sucursal';

      // Preparar los datos con encabezados específicos
      const excelData = this.reportData.map(item => ({
        'ID Insumo': item.supply_id,
        'Nombre Insumo': item.supply_name,
        'Categoría': item.supply_category,
        'Unidad': item.supply_unit,
        'Precio Unitario': item.supply_price,
        'Vendible': item.supply_trackable,
        'Cantidad Inicial': item.initial_qty,
        'Costo Inicial': item.initial_cost,
        'Cantidad Movimientos': item.stock_movements_qty,
        'Costo Movimientos': item.stock_movements_total,
        'Cantidad Compras': item.purchase_order_qty,
        'Costo Compras': item.purchase_order_total,
        'Cantidad Ventas': item.customer_out_qty,
        'Costo Ventas': item.customer_out_total,
        'Inventario Teórico': item.theoretical_qty,
        'Costo Teórico': item.theoretical_total,
        'Cantidad Final': item.end_qty,
        'Costo Final': item.end_cost,
        'Diferencia Cantidad': item.difference_qty,
        'Diferencia Costo': item.difference_total,
        'Sucursal': sucursalName
      }));

      // Crear una hoja de cálculo
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Obtener el rango de la tabla
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      // Configurar anchos de columna
      const columnWidths = [
        { wch: 10 },  // ID Insumo
        { wch: 30 },  // Nombre Insumo
        { wch: 15 },  // Categoría
        { wch: 10 },  // Unidad
        { wch: 15 },  // Precio Unitario
        { wch: 10 },  // Vendible
        { wch: 15 },  // Cantidad Inicial
        { wch: 15 },  // Costo Inicial
        { wch: 18 },  // Cantidad Movimientos
        { wch: 18 },  // Costo Movimientos
        { wch: 15 },  // Cantidad Compras
        { wch: 15 },  // Costo Compras
        { wch: 15 },  // Cantidad Ventas
        { wch: 15 },  // Costo Ventas
        { wch: 18 },  // Inventario Teórico
        { wch: 15 },  // Costo Teórico
        { wch: 15 },  // Cantidad Final
        { wch: 15 },  // Costo Final
        { wch: 18 },  // Diferencia Cantidad
        { wch: 15 },  // Diferencia Costo
        { wch: 20 }   // Sucursal
      ];
      worksheet['!cols'] = columnWidths;

      // Configurar formato de tabla con autofiltro
      worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(range) };

      // Crear un libro de trabajo
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte de Inventario");

      // Generar archivo Excel
      const initialDateText = this.getCountGroupDate(this.selectedInitialCount);
      const endDateText = this.getCountGroupDate(this.selectedEndCount);
      const fileName = `Reporte_Inventario_${sucursalName}_${initialDateText}_a_${endDateText}.xlsx`;

      XLSX.writeFile(workbook, fileName);
    },
    getCountGroupDate(countGroupId) {
      const group = this.countGroups.find(g => g.value === countGroupId);
      if (group) {
        // Extraer solo la parte de la fecha (asumiendo formato "Nombre (YYYY-MM-DD)")
        const match = group.text.match(/\(([^)]+)\)/);
        return match ? match[1] : "desconocido";
      }
      return "desconocido";
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '0';
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
      });
    }
  }
};
</script>

<style scoped>
.summary-item {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-bottom: 10px;
}

.summary-label {
  font-weight: bold;
  font-size: 0.9rem;
  color: #495057;
}

.summary-value {
  font-size: 1.2rem;
  font-weight: 600;
  color: #343a40;
}

.total-difference {
  padding: 12px;
  background-color: #e9ecef;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.total-difference-label {
  font-weight: bold;
  font-size: 1.1rem;
  margin-right: 10px;
}

.total-difference-value {
  font-size: 1.3rem;
  font-weight: 700;
  margin-right: 10px;
}

.total-difference-percent {
  font-size: 0.9rem;
  font-style: italic;
}

.text-danger .total-difference-value,
.text-danger .total-difference-percent {
  color: #dc3545;
}

.text-success .total-difference-value,
.text-success .total-difference-percent {
  color: #28a745;
}

/* Shifts validation card styles */
.card .card-title {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.card .card-title i {
  margin-right: 0.5rem;
}

.list-group-item {
  background-color: rgba(255, 255, 255, 0.9);
  color: #333;
  border: 1px solid rgba(0, 0, 0, 0.125);
}

/* Ajustes para tabla responsive */
@media (max-width: 768px) {
  .b-table-stacked-md th {
    display: none;
  }
}
</style>